import { useCallback, useContext, useEffect, useReducer, useRef } from 'react';

import { match, P } from 'ts-pattern';

import { useAIEventsInstrumentation } from '@atlassian/ai-analytics';
import { SharedFeaturesInstrumentationIdContext } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics-properties-context';
import { useUFOStreamingExperience } from '@atlassian/ufo-experimental/use-ufo-streaming-experience';

import { voiceDictationReducer } from '../effects/voiceDictationReducer';
import { WebSpeechToTextManager } from '../effects/WebSpeechToTextManager';
import {
	type VoiceDictationResult,
	VoiceDictationState,
	VoiceDictationStopReason,
	type WebSpeechToTextTraceAction,
} from '../effects/WebSpeechToTextModels';

// eslint-disable-next-line @repo/internal/deprecations/deprecation-ticket-required -- Migration guidance is documented in the speech-to-text README.
/**
 * @deprecated See the {@link https://bitbucket.org/atlassian/atlassian-frontend-monorepo/src/master/platform/packages/insights-ai-platform/ai-audio/src/speech-to-text/README.md speech-to-text README} for information about the new API.
 */
export const useVoiceDictation = (
	onVoiceDictationUpdate: (result: VoiceDictationResult) => void,
	cloudID: string,
	{
		audioBitsPerSecond = 16000,
		sampleRate = 24000,
		timeSlice = 1000,
		maxRecordingDurationMs = 60000,
		enableGhostText: enableGhostTextOption,
		product,
		experience,
		vadType,
		eagerness,
		model,
		skipSessionUpdate,
	}: {
		audioBitsPerSecond?: number;
		sampleRate?: number;
		timeSlice?: number;
		maxRecordingDurationMs?: number;
		enableGhostText?: boolean;
		product?: string;
		experience?: string;
		vadType?: string;
		eagerness?: string;
		/**
		 * Transcription model to request in `audio.session.update`. When
		 * omitted, the server uses its default (currently `gpt-4o-transcribe`).
		 *
		 * Note: only takes effect when `skipSessionUpdate` is false (the
		 * default), because the value is delivered via the session update.
		 */
		model?: string;
		/**
		 * When `true`, the WebSpeechToTextManager will NOT send an
		 * `audio.session.update` after the WebSocket connects. The server-side
		 * session will run with its default model + VAD config (driven by a
		 * server-side feature flag). Useful for measuring baseline performance
		 * or working around gateway-side session.update issues.
		 *
		 * Note: when `true`, the `vadType` and `eagerness` options are no-ops
		 * because they are only delivered via `audio.session.update`.
		 *
		 * @default false
		 */
		skipSessionUpdate?: boolean;
	} = {},
): [
	VoiceDictationResult,
	{
		startRecording: () => void;
		stopRecording: (stopReason?: VoiceDictationStopReason) => void;
	},
] => {
	const { sharedFeaturesInstrumentationId, isInSmartCreateFlow } = useContext(
		SharedFeaturesInstrumentationIdContext,
	);

	const {
		trackAIInteractionInit,
		trackAIResultView,
		trackAIResultError,
		trackAIInteractionDismiss,
		trackAIResultAction,
		trackAIResultDiscarded,
	} = useAIEventsInstrumentation({
		product: 'platform',
		subproduct: 'platform',
		aiFeatureName: 'aiVoiceDictation',
		proactiveGeneratedAI: 0,
		userGeneratedAI: 1,
		enablePlatformEvents: true,
		additionalAttributes: {
			isLegacyVersion: true,
			productAttributionOverride: product ?? 'confluence',
			sharedFeaturesInstrumentationId,
			isInSmartCreateFlow,
		},
	});

	const {
		start,
		success,
		startConnectionAttempt,
		successConnectionEstablished,
		reportTTFB,
		reportRTT,
		failure,
	} = useUFOStreamingExperience('streaming-stt', 'ai-speech-to-text');

	const [result, dispatch] = useReducer(voiceDictationReducer, {
		state: VoiceDictationState.Uninitialized,
		transcription: null,
	});

	const trace = useCallback(
		(action: WebSpeechToTextTraceAction) => {
			match(action)
				.with({ type: 'START_WEBSOCKET_CONNECTION' }, () => {
					start();
					startConnectionAttempt();
				})
				.with({ type: 'WEBSOCKET_CONNECTION_SUCCESS' }, () => {
					successConnectionEstablished();
				})
				.with({ type: 'TRANSCRIPTION_ITEM_CREATED', payload: { createdItemChunkIndex: 1 } }, () => {
					// Time to First Byte for first chunk
					reportTTFB();
				})
				.with(
					{
						type: 'TRANSCRIPTION_ITEM_CREATED',
						payload: { createdItemChunkIndex: P.number },
					},
					({ payload: { createdItemChunkIndex } }) => {
						// Round-trip time between received server messages
						reportRTT(createdItemChunkIndex);
					},
				)
				.with({ type: 'STOP_RECORDING' }, (stopAction) => {
					success({ metadata: { stopReason: stopAction.payload.stopReason } });
				})
				.with({ type: 'ERROR' }, (errorAction) => {
					failure({ metadata: { error: errorAction.payload.error } });
				})
				.exhaustive();
		},
		[
			reportRTT,
			reportTTFB,
			start,
			startConnectionAttempt,
			successConnectionEstablished,
			failure,
			success,
		],
	);

	const speechToTextManager = useRef(
		new WebSpeechToTextManager({
			onTranscription: (action) => dispatch(action),
			onTrace: (action) => trace(action),
			cloudID,
			sampleRate,
			audioBitsPerSecond,
			timeSlice,
			maxRecordingDurationMs,
			product,
			experience,
			vadType,
			eagerness,
			model,
			skipSessionUpdate,
		}),
	);

	useEffect(() => {
		onVoiceDictationUpdate(result);
	}, [result, onVoiceDictationUpdate]);

	useEffect(() => {
		match(result)
			.with({ state: VoiceDictationState.Initializing }, () => trackAIInteractionInit())
			.with({ state: VoiceDictationState.Recording, chunkIndex: 1 }, () => trackAIResultView())
			.with(
				{
					state: VoiceDictationState.Stopped,
					stopReason: VoiceDictationStopReason.StoppedAccepted,
				},
				() => trackAIResultAction('STTAcceptedByUser'),
			)
			.with(
				{
					state: VoiceDictationState.Stopped,
					stopReason: VoiceDictationStopReason.StoppedRejected,
				},
				() => trackAIResultDiscarded('STTRejectedByUser'),
			)
			.with(
				{
					state: VoiceDictationState.Stopped,
					stopReason: VoiceDictationStopReason.TimeoutAccepted,
				},
				() => trackAIResultAction('STTAcceptedDueToTimeout'),
			)
			.with({ state: VoiceDictationState.Error }, (errorResult) =>
				trackAIResultError({ aiErrorMessage: errorResult.error }),
			)
			.with(P._, () => {})
			.exhaustive();
	}, [
		result,
		trackAIInteractionInit,
		trackAIInteractionDismiss,
		trackAIResultView,
		trackAIResultError,
		trackAIResultAction,
		trackAIResultDiscarded,
	]);

	const startRecording = useCallback(() => {
		speechToTextManager.current.startRecording();
	}, []);

	const stopRecording = useCallback(
		(stopReason: VoiceDictationStopReason = VoiceDictationStopReason.StoppedAccepted) => {
			speechToTextManager.current.stopRecording(stopReason);
		},
		[],
	);

	useEffect(() => {
		return () => stopRecording();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return [result, { startRecording, stopRecording }];
};
